import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { guid } from '@/mixins/guid';

export default Vue.component('splash', {
  template: require('./template.html'),
  name: 'splash',
  mixins: [guid],
  computed: {
    ...mapGetters([
      'fetchError',
      'account',
      'priceSource',
      'userAttributes',
      'firstLogin',
      'currentUser',
      'websites',
      'identityId',
      'ticketUsers',
    ]),
  },
  created() {
    this.checkLogin();
  },
  methods: {
    async checkLogin() {
      try {
        const session = await this._cognitoValidateSession();
        // find route to go next
        if (session) {
          await this.loadNextRoute();
        }
      } catch (e) {
        this.$log.error('error: ', e);
        this.$store.dispatch('addNotification', {
          id: this.guid(),
          type: 'error',
          message: `${e.message || e}. This has been reported.`,
        });
        await this._reportError({
          error: e,
          fn: 'checkLogin',
        });
      }
    },
    async loadNextRoute() {
      try {
        const data = await this.getPricingData();
        this.$store.dispatch('setPriceSource', {
          data: data.body,
        });
        this.$store.dispatch('setFetchError', false);

        if (!this._storm) {
          if (this.firstLogin) {
            this.$log.info('First time login!!');
            // customer account does not exist yet on the first login
            // so we need to create it then get it
            await this._setUpFirstLogin(this.currentUser);
          }

          // get account info from dynamo
          let account = await this._getUserData(this.currentUser);
          const fullName = this.currentUser.fullName;

          // we need to check if user is first logging in from a social account
          const userInPool = await this._cognitoAmplifyGetUser();
          const userAttrs = userInPool.attributes;
          let provider = 'Cognito';
          if (userAttrs.identities) {
            const identities = JSON.parse(userInPool.attributes.identities)[0];
            provider = identities.providerName;
          }

          if (!account.status) {
            this.$log.info('fallback first login');
            // If we reach here, we need a safe gaurd in the event
            // customer data was not set up on first time login. we need to
            // go add it in dynamo then send to splash again
            await this._setUpFirstLogin(this.currentUser);
            account = await this._fallbackGetUserData(this.currentUser);

            if (account.owner) {
              await this.firstLoginOwnerSetup({ provider, fullName });
            }
          }

          // finish setting up first login logic
          if (this.firstLogin && account.owner) {
            await this.firstLoginOwnerSetup({ provider, fullName });
          }

          this.$store.dispatch('setFirstLogin', false);

          // grab attrs for currentUser settings
          this.$store.dispatch('setCurrentUser', { values: _.cloneDeep(account), vm: this });
          this.hydrateWebsites();
          this.$store.dispatch('setTicketUser', {
            id: this.currentUser._id,
            data: {
              _id: this.currentUser._id,
              pic: this.currentUser.pic,
              fullName: this.currentUser.fullName,
            },
          });

          this.$store.dispatch('setAccount', account);
          // set the users status
          const status = account._account_owner_status || account.status;
          this.$store.dispatch('setAccountStatus', { status });

          const isSubUser = this.currentUser.subuser;
          // force user to account settings to update payment if delinquent
          if (status === 'delinquent' || status === 'expired') {
            const path = isSubUser ? '/blocked' : '/billing';
            this.$router.push({ path });
            return;
          }
          if (status === 'disabled') {
            this.$router.push({ path: '/blocked' });
            return;
          }
          // load in a cached route if there is one
          const path = window.localStorage.getItem('lastRoute');
          if (path && path.trim() !== '') {
            this.$log.debug('load last route: ', path);
            this.$router.push({ path });
          } else {
            this.$router.push({ path: '/websites' });
          }
          // warn user if they have an unpaid balance
          const $vm = this;
          if (status === 'balance_unpaid' && !isSubUser) {
            setTimeout(() => {
              this.$store.dispatch('addNotification', {
                id: this.guid(),
                type: 'warning',
                message: `
                You have a passed due balance, please update your payment information so
                you won't risk any site downtime!`,
                action: 'Go to Billing',
                actionFn() {
                  $vm.$router.push({ path: '/billing' });
                },
              });
            }, 1000);
            return;
          }
        }
        if (this._storm) {
          const path = window.localStorage.getItem('lastStormRoute');
          if (path && path.trim() !== '') {
            this.$router.push({ path });
          } else {
            this.$router.push({ path: '/storm-drzzle-accounts' });
          }
        }
      } catch (e) {
        const message = e.message || e;
        this.$log.error('error: ', message);
        this.$store.dispatch('setFetchError', message);
        await this._reportError({
          error: e,
          fn: 'loadNextRoute',
        });
      } finally {
        if (!this.currentUser.identity_id && !this._storm) {
          this.$lightning([{ name: 'mapUserIdentity' }]);
        }
        this._trackEvent({
          type: 'login',
        });
      }
    },
    async hydrateWebsites() {
      try {
        this.$store.dispatch('setGlobalLoader', {
          type: 'websites',
          value: true,
        });
        const { websites } = await this.$lightning([{ name: 'hydrateWebsites' }]);
        this.$log.debug('user site list: ', websites);
        this.$store.dispatch('setWebsites', { websites });
        this.$store.dispatch('setGlobalValue', {
          item: this.currentUser,
          key: 'websites',
          value: websites,
        });
        this.$store.dispatch('setGlobalValue', {
          item: this.account,
          key: 'websites',
          value: websites,
        });
      } catch (e) {
        const message = e.message || e;
        this.$log.error('error: ', message);
        await this._reportError({
          error: e,
          fn: 'hydrateWebsites',
        });
      } finally {
        this.$store.dispatch('setGlobalLoader', {
          type: 'websites',
          value: false,
        });
      }
    },
    async firstLoginOwnerSetup(params) {
      const ownerSetup = await this._SEND([{
        name: 'ownerFirstLogin',
        payload: {
          provider: params.provider,
          fullName: params.fullName,
        },
      }]);

      this.$log.debug('firstLoginOwnerSetup: ', ownerSetup);
      // update new stripe id in user state
      this.$store.dispatch('setUserAttribute', { stripeId: ownerSetup.stripeId });
      return ownerSetup;
    },
    getPricingData() {
      return this.$http.get(`${process.env.S3_ASSETS_BUCKET}info/pricing-data.json?${Date.now()}`);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/splash/index.js